import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "@/hooks/use-toast"
import { useAuthContext } from "@/context/authentication/auth-context"
import { useRegisterBankDetails, useUpdateJuridicPerson } from "@/services/queries/usePerson"
import type { FieldConfig } from "@/components/form-builder"
import { personPJSchema } from "../schemas/person-pj-schema"
import type { PersonPJFormData } from "../types/person-pj"
import { transformPJFormToBank, transformPJFormToJuridic } from "../utils/person-transformers"
import { useViaCep } from "@/services/queries/useViaCep"
import { useEffect } from "react"

interface UsePersonPJAddProps {
  onSuccess: () => void
}

export function usePersonPJAdd({ onSuccess }: UsePersonPJAddProps) {
  const form = useForm<PersonPJFormData>({
    resolver: zodResolver(personPJSchema),
    defaultValues: {
      companyName: "",
      cnpj: "",
      businessAddress: "",
      businessDistrict: "",
      businessCityState: "",
      businessZipCode: "",
      cnaeCode: "",
      municipalRegistration: "",
      ancineRegistrationNumber: "",
      bankName: "",
      bankBranch: "",
      checkingAccount: "",
      pixKey: "",
    },
  })

  const { data: address } = useViaCep({ zipCode: form.watch("businessZipCode") })

  useEffect(() => {
    if (address) {
      form.setValue("businessAddress", address.logradouro, { shouldValidate: true })
      form.setValue("businessDistrict", address.bairro, { shouldValidate: true })
      form.setValue("businessCityState", `${address.localidade}, ${address.uf}`, { shouldValidate: true })
    }
  }, [address, form])

  const { person } = useAuthContext()

  useEffect(() => {
    if (person?.juridic_person) {
      const jp = person.juridic_person
      form.reset({
        companyName: jp.company_name,
        cnpj: jp.document,
        businessAddress: jp.address,
        businessDistrict: jp.neighbourhood,
        businessCityState: jp.city_state,
        businessZipCode: jp.postal_code,
        cnaeCode: jp.cnaes,
        municipalRegistration: jp.municipal_register,
        ancineRegistrationNumber: jp.ancine_number,
        // Bank details might be in a separate array, checking person.bank_accounts
        bankName: person.bank_accounts?.[0]?.bank_name || "",
        bankBranch: "", // Not in IBankAccount interface?
        checkingAccount: person.bank_accounts?.[0]?.bank_account || "",
        pixKey: "", // Not in IBankAccount interface?
      })
    }
  }, [person, form])

  const updateJuridic = useUpdateJuridicPerson(false)
  const registerBank = useRegisterBankDetails(false)

  const onFormSubmit = async (data: PersonPJFormData) => {

    try {
      await updateJuridic.mutateAsync(transformPJFormToJuridic(data))
      await registerBank.mutateAsync(transformPJFormToBank(data))

      toast({
        title: "Dados salvos com sucesso",
        variant: "success",
      })
      onSuccess()
    } catch (error) {
      console.error("Erro ao salvar dados PJ:", error)
      toast({
        title: "Erro ao salvar dados",
        description: "Não foi possível salvar os dados da empresa.",
        variant: "destructive",
      })
    }
  }

  const companyFields: FieldConfig[] = [
    {
      name: "companyName",
      type: "text",
      label: "Razão Social",
      placeholder: "Digite aqui",
      required: true
    },
    {
      name: "cnpj",
      type: "cnpj",
      label: "CNPJ",
      placeholder: "Digite o CNPJ",
      required: true
    },
    {
      name: "businessAddress",
      type: "text",
      label: "Endereço empresarial (PJ)",
      placeholder: "Digite aqui",
      required: true,
    },
    {
      name: "businessDistrict",
      type: "text",
      label: "Bairro (PJ)",
      placeholder: "Digite aqui",
      required: true,
    },
    {
      name: "businessCityState",
      type: "text",
      label: "Cidade, Estado (PJ)",
      placeholder: "Digite aqui",
      required: true,
    },
    {
      name: "businessZipCode",
      type: "cep",
      label: "CEP (PJ)",
      placeholder: "00000-000",
      required: true,
    },
    {
      name: "cnaeCode",
      type: "text",
      label: "CNAEs",
      placeholder: "Digite aqui",
      required: true
    },
    {
      name: "municipalRegistration",
      type: "text",
      label: "Inscrição Municipal",
      placeholder: "Digite aqui"
    },
    {
      name: "ancineRegistrationNumber",
      type: "text",
      label: "Número de cadastro na ANCINE (o registro de agente econômico é obrigatório para produtor proponente na plataforma)",
      placeholder: "Digite aqui",
      colSpan: "full",
      required: true
    },
  ]

  const bankFields: FieldConfig[] = [
    {
      name: "bankName",
      type: "text",
      label: "Banco",
      placeholder: "Digite aqui"
    },
    {
      name: "bankBranch",
      type: "text",
      label: "Agência",
      placeholder: "Digite aqui"
    },
    {
      name: "checkingAccount",
      type: "text",
      label: "Conta Corrente",
      placeholder: "Digite aqui"
    },
    {
      name: "pixKey",
      type: "text",
      label: "Chave Pix",
      placeholder: "Digite aqui"
    },
  ]

  const isPending = updateJuridic.isPending || registerBank.isPending

  return {
    form,
    isPending,
    onFormSubmit,
    companyFields,
    bankFields,
  }
}
